import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Extension } from '@tiptap/core'
import { Bold, Italic, List, Link2, Code, Palette, Type } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'

// カスタムFontSize拡張
const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ commands }: { commands: any }) => {
        return commands.setMark('textStyle', { fontSize })
      },
      unsetFontSize: () => ({ commands }: { commands: any }) => {
        return commands.unsetMark('textStyle')
      },
    } as any
  },
})

// 利用可能な色
const COLORS = [
  { name: '黒', value: '#000000' },
  { name: '灰', value: '#6B7280' },
  { name: '赤', value: '#DC2626' },
  { name: 'オレンジ', value: '#EA580C' },
  { name: '黄', value: '#CA8A04' },
  { name: '緑', value: '#16A34A' },
  { name: '青', value: '#2563EB' },
  { name: '紫', value: '#9333EA' },
  { name: 'ピンク', value: '#DB2777' },
]

// 利用可能なフォントサイズ
const FONT_SIZES = [
  { name: '小', value: '12px' },
  { name: '標準', value: '14px' },
  { name: '中', value: '16px' },
  { name: '大', value: '18px' },
  { name: '特大', value: '24px' },
]

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
}

export default function RichTextEditor({ content, onChange, placeholder, className }: RichTextEditorProps) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showFontSizePicker, setShowFontSizePicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)
  const fontSizePickerRef = useRef<HTMLDivElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // タスク詳細では見出しは不要
        hardBreak: {
          keepMarks: true,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'タスクの詳細を入力...',
      }),
      TextStyle,
      Color,
      FontSize,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[100px] px-3 py-2 bg-white',
      },
      // ペースト時にプレーンテキストの改行を保持
      transformPastedText(text) {
        // プレーンテキストの改行を保持（TipTapのデフォルト処理で段落に分割される）
        return text
      },
      transformPastedHTML(html) {
        // HTMLの改行タグを保持
        return html
      },
    },
    // ペースト時に段落を分割するように設定
    parseOptions: {
      preserveWhitespace: 'full',
    },
  })

  // コンテンツが外部から変更された場合に同期
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [content, editor])

  // ピッカー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false)
      }
      if (fontSizePickerRef.current && !fontSizePickerRef.current.contains(event.target as Node)) {
        setShowFontSizePicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!editor) {
    return null
  }

  const addLink = () => {
    const url = window.prompt('リンクURLを入力してください:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const setColor = (color: string) => {
    ;(editor.chain().focus() as any).setColor(color).run()
    setShowColorPicker(false)
  }

  const setFontSize = (size: string) => {
    (editor.chain().focus() as any).setFontSize(size).run()
    setShowFontSizePicker(false)
  }

  // 現在のテキスト色を取得
  const currentColor = editor.getAttributes('textStyle').color || '#000000'

  return (
    <div className={`border border-gray-300 rounded-lg ${className}`}>
      {/* ツールバー */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-wrap">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('bold') ? 'bg-gray-300' : ''
          }`}
          title="太字 (Ctrl+B)"
          type="button"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('italic') ? 'bg-gray-300' : ''
          }`}
          title="斜体 (Ctrl+I)"
          type="button"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('code') ? 'bg-gray-300' : ''
          }`}
          title="コード"
          type="button"
        >
          <Code className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1"></div>

        {/* カラーピッカー */}
        <div className="relative" ref={colorPickerRef}>
          <button
            onClick={() => {
              setShowColorPicker(!showColorPicker)
              setShowFontSizePicker(false)
            }}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-0.5"
            title="文字色"
            type="button"
          >
            <Palette className="h-4 w-4" />
            <span
              className="w-3 h-3 rounded-sm border border-gray-300"
              style={{ backgroundColor: currentColor }}
            />
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999]" style={{ width: '120px' }}>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setColor(color.value)}
                    className="rounded border border-gray-200 hover:opacity-80 flex-shrink-0"
                    style={{ backgroundColor: color.value, width: '28px', height: '28px' }}
                    title={color.name}
                    type="button"
                  />
                ))}
              </div>
              <button
                onClick={() => {
                  ;(editor.chain().focus() as any).unsetColor().run()
                  setShowColorPicker(false)
                }}
                className="w-full mt-2 text-xs text-gray-500 hover:text-gray-700"
                type="button"
              >
                色をリセット
              </button>
            </div>
          )}
        </div>

        {/* フォントサイズ */}
        <div className="relative" ref={fontSizePickerRef}>
          <button
            onClick={() => {
              setShowFontSizePicker(!showFontSizePicker)
              setShowColorPicker(false)
            }}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center gap-0.5"
            title="文字サイズ"
            type="button"
          >
            <Type className="h-4 w-4" />
          </button>
          {showFontSizePicker && (
            <div className="absolute top-full left-0 mt-1 p-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] min-w-[80px]">
              {FONT_SIZES.map((size) => (
                <button
                  key={size.value}
                  onClick={() => setFontSize(size.value)}
                  className="w-full px-3 py-1 text-left text-sm hover:bg-gray-100 rounded"
                  style={{ fontSize: size.value }}
                  type="button"
                >
                  {size.name}
                </button>
              ))}
              <button
                onClick={() => {
                  (editor.chain().focus() as any).unsetFontSize().run()
                  setShowFontSizePicker(false)
                }}
                className="w-full mt-1 pt-1 border-t text-xs text-gray-500 hover:text-gray-700"
                type="button"
              >
                サイズをリセット
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300 mx-1"></div>

        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('bulletList') ? 'bg-gray-300' : ''
          }`}
          title="箇条書き"
          type="button"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={addLink}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('link') ? 'bg-gray-300' : ''
          }`}
          title="リンク"
          type="button"
        >
          <Link2 className="h-4 w-4" />
        </button>
        {editor.isActive('link') && (
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
            type="button"
          >
            リンク解除
          </button>
        )}
      </div>

      {/* エディタ */}
      <EditorContent editor={editor} />
    </div>
  )
}
